import { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

// Shared term-mode helpers: the term period picker + "counts toward term" checkbox used by the
// quiz close-and-push UI and the "Term settings" editor on assessment views.
//
// Assessments no longer carry their own weight in the term: every ordinary assessment in a
// subject/term is pooled automatically (Σobtained / Σmax), scaled by whatever share the term
// paper doesn't take. There is nothing left to allocate or cap here.

const fieldLbl = { display:'block', fontSize:'.68rem', fontWeight:700, color:'#6b7194', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 };
const fieldInp = { width:'100%', padding:'9px 12px', border:'1.5px solid #e4e8f5', borderRadius:10, fontFamily:'inherit', fontSize:'.82rem', color:'#1a1d2e', background:'white', boxSizing:'border-box' };

// value = { termPeriodId, countsTowardTerm }
export const TermSettingsFields = ({ value, onChange, terms }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div>
        <label style={fieldLbl}>Term Period</label>
        <select style={fieldInp} value={value.termPeriodId || ''}
          onChange={e => set({ termPeriodId: e.target.value })}>
          <option value="">No term (standalone entry)</option>
          {(terms || []).map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      </div>
      {value.termPeriodId && (
        <div>
          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
            <input type="checkbox" checked={!!value.countsTowardTerm}
              onChange={e => set({ countsTowardTerm: e.target.checked })} />
            <span style={{fontSize:'.8rem',fontWeight:600,color:'#1a1d2e'}}>Count this toward the term's %</span>
          </label>
        </div>
      )}
    </div>
  );
};

// Payload helper: normalises form values for the backend.
export const termPayload = (v) => ({
  termPeriodId: v.termPeriodId || '',
  countsTowardTerm: !!v.countsTowardTerm,
});

// Compact "Term settings: 1st Bi-Monthly · counts  [Edit]" line + modal editor.
// role: 'teacher' | 'admin'.  onSaved(newSettings) lets the parent patch its local record.
export const TermSettingsEditor = ({ assessment, terms, role, onSaved }) => {
  const [open, setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm]   = useState({ termPeriodId:'', countsTowardTerm:true });

  const curTermId = assessment.termPeriodId ? String(assessment.termPeriodId) : '';
  const term = curTermId ? (terms || []).find(t => String(t._id) === curTermId) : null;
  const summary = curTermId
    ? [
        term?.name || 'Term',
        assessment.countsTowardTerm === false ? 'not counted' : 'counts',
      ].join(' · ')
    : 'Standalone (no term)';

  const openEditor = () => {
    setForm({
      termPeriodId: curTermId,
      countsTowardTerm: assessment.countsTowardTerm !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.put(`/${role}/assessments/${assessment._id || assessment.id}/term-settings`, termPayload(form));
      toast.success('Term settings updated');
      setOpen(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update term settings');
    } finally { setSaving(false); }
  };

  // Offer active terms, plus the assessment's current term even if it was deactivated.
  const options = (terms || []).filter(t => t.active || String(t._id) === curTermId || String(t._id) === String(form.termPeriodId));

  return (
    <>
      <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6,flexWrap:'wrap',fontSize:'.76rem',color:'#6b7194'}}>
        <span style={{fontWeight:600,color:'#9094a8'}}>Term settings:</span>
        <span style={{fontWeight:600,color:'#3730a3',background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:8,padding:'2px 9px'}}>{summary}</span>
        <button type="button" onClick={openEditor}
          style={{border:'none',background:'none',color:'#3b6ef8',fontWeight:700,fontSize:'.74rem',cursor:'pointer',fontFamily:'inherit',padding:0}}>
          Edit
        </button>
      </div>
      {open && createPortal(
        <div style={{position:'fixed',inset:0,background:'rgba(15,17,35,.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
          onClick={e => { if (e.target === e.currentTarget && !saving) setOpen(false); }}>
          <div style={{background:'white',borderRadius:16,padding:'20px 22px',width:'100%',maxWidth:440,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.25)',fontFamily:"'Sora',sans-serif"}}>
            <div style={{fontSize:'1rem',fontWeight:700,color:'#0f1123',marginBottom:4}}>Term settings</div>
            <div style={{fontSize:'.74rem',color:'#9094a8',marginBottom:14}}>
              {assessment.assessmentType?.name} — {assessment.subject}. Can be changed anytime, even after marks are locked.
            </div>
            <TermSettingsFields value={form} onChange={setForm} terms={options} />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:18}}>
              <button type="button" disabled={saving} onClick={() => setOpen(false)}
                style={{padding:'9px 16px',border:'none',borderRadius:10,background:'#f0f3ff',color:'#6b7194',fontWeight:600,fontSize:'.8rem',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
              <button type="button" disabled={saving} onClick={save}
                style={{padding:'9px 16px',border:'none',borderRadius:10,background:'#3b6ef8',color:'white',fontWeight:600,fontSize:'.8rem',cursor:'pointer',fontFamily:'inherit'}}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
